import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
} from '@heroui/react';
import { Globe } from 'lucide-react';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 6px;
  background: linear-gradient(135deg, #1a4d4d 0%, #0f3333 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
`;

const Description = styled.p`
  font-size: 0.95rem;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#9ca3af' : '#6b7280')};
  line-height: 1.5;
  margin-bottom: 20px;
`;

const LANGUAGE_NAMES: Record<string, string> = {
  pl: 'Polski',
  en: 'English',
  de: 'Deutsch',
  es: 'Español',
  fr: 'Français',
  it: 'Italiano',
  cz: 'Čeština',
  sk: 'Slovenčina',
  ua: 'Українська',
  ru: 'Русский',
  gr: 'Ελληνικά',
  se: 'Svenska',
  no: 'Norsk',
};

interface ModalSelectLanguageProps {
  isOpen: boolean;
  onClose?: () => void;
  onConfirm: (language: string) => void;
  availableLanguages: string[];
  defaultLanguage?: string;
  isLoading?: boolean;
  isDismissable?: boolean;
}

export const ModalSelectLanguage: FC<ModalSelectLanguageProps> = ({
  isOpen,
  onClose,
  onConfirm,
  availableLanguages,
  defaultLanguage,
  isLoading = false,
  isDismissable = false,
}) => {
  const { t } = useTranslation();
  const [selectedLanguage, setSelectedLanguage] = useState<string>(
    defaultLanguage || availableLanguages[0] || 'en',
  );

  const handleConfirm = () => {
    if (selectedLanguage) {
      onConfirm(selectedLanguage);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onClose}
      backdrop="opaque"
      size="md"
      isDismissable={isDismissable && !isLoading}
      hideCloseButton={!isDismissable || isLoading}
      classNames={{
        backdrop: 'bg-black/50',
        base: 'bg-white dark:bg-[#1a1f26] rounded-md shadow-xl',
      }}
    >
      <ModalContent>
        {(onModalClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1 pb-2 pt-6 px-6">
              <IconWrapper>
                <Globe size={24} className="text-white" />
              </IconWrapper>
              <h3 className="text-xl font-light text-gray-900 dark:text-white">
                {t('language.selectLanguage')}
              </h3>
            </ModalHeader>

            <ModalBody className="px-6">
              <Description>
                {t('language.selectLanguageDescription')}
              </Description>

              <Select
                label={t('language.systemLanguage')}
                placeholder={t('language.chooseLanguage')}
                selectedKeys={selectedLanguage ? [selectedLanguage] : []}
                onSelectionChange={(keys) => {
                  const selected = Array.from(keys)[0] as string;
                  setSelectedLanguage(selected);
                }}
                variant="bordered"
                radius="sm"
                size="md"
                isDisabled={isLoading}
                classNames={{
                  trigger: 'min-h-12 py-2',
                }}
              >
                {availableLanguages.map((lang) => (
                  <SelectItem key={lang}>
                    {LANGUAGE_NAMES[lang] || lang.toUpperCase()}
                  </SelectItem>
                ))}
              </Select>
            </ModalBody>

            <ModalFooter className="px-6 pb-6 pt-4">
              {isDismissable && (
                <Button
                  color="default"
                  variant="flat"
                  onPress={onModalClose}
                  isDisabled={isLoading}
                >
                  {t('common.cancel')}
                </Button>
              )}
              <Button
                color="primary"
                onPress={handleConfirm}
                isLoading={isLoading}
                isDisabled={!selectedLanguage}
                className="bg-[#1a4d4d] hover:bg-[#0f3333]"
              >
                {t('common.confirm')}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
